import React from "react";
import { Container, Row, Col, Card, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { 
  FaUserTie, 
  FaUsers, 
  FaCode, 
  FaMobile, 
  FaBullhorn, 
  FaChartLine,
  FaCogs,
  FaShieldAlt,
  FaLightbulb,
  FaHandshake
} from "react-icons/fa";
import "../assets/styles/RolesPage.css";

const RolesPage = () => {
  const navigate = useNavigate();

  const handleViewDetails = (roleId) => {
    navigate(`/team-details/${roleId}`);
  };

  const roles = [
    {
      id: 1,
      title: "Chief Executive Officer (CEO)",
      icon: <FaUserTie size={48} />,
      category: "Leadership",
      description: "Strategic leadership and vision for the company, driving growth and innovation across all business areas.",
      responsibilities: [
        "Strategic planning and business development",
        "Team leadership and company culture",
        "Financial oversight and investor relations",
        "Market expansion and partnerships"
      ],
      requirements: [
        "15+ years of executive experience",
        "Proven track record in tech industry",
        "Strong leadership and communication skills",
        "Strategic thinking and decision-making"
      ]
    },
    {
      id: 2,
      title: "Project Manager",
      icon: <FaUsers size={48} />,
      category: "Management",
      description: "Overseeing project execution, team coordination, and ensuring successful delivery of client projects.",
      responsibilities: [
        "Project planning and resource allocation",
        "Team coordination and stakeholder management",
        "Risk assessment and mitigation",
        "Quality assurance and delivery"
      ],
      requirements: [
        "8+ years of project management experience",
        "PMP or Agile certification preferred",
        "Excellent communication and leadership",
        "Technical background in software development"
      ]
    },
    {
      id: 3,
      title: "Human Resources Manager",
      icon: <FaHandshake size={48} />,
      category: "HR",
      description: "Managing talent acquisition, employee development, and fostering a positive workplace culture.",
      responsibilities: [
        "Recruitment and talent acquisition",
        "Employee relations and performance management",
        "Training and development programs",
        "HR policies and compliance"
      ],
      requirements: [
        "5+ years of HR experience",
        "Bachelor's degree in HR or related field",
        "Strong interpersonal and communication skills",
        "Knowledge of employment laws and regulations"
      ]
    },
    {
      id: 4,
      title: "Full Stack Developer",
      icon: <FaCode size={48} />,
      category: "Development",
      description: "Building end-to-end web applications with expertise in both frontend and backend technologies.",
      responsibilities: [
        "Frontend and backend development",
        "Database design and API development",
        "Code review and technical documentation",
        "Performance optimization and testing"
      ],
      requirements: [
        "3+ years of full stack development",
        "Proficiency in React, Node.js, and databases",
        "Experience with cloud platforms",
        "Strong problem-solving skills"
      ]
    },
    {
      id: 5,
      title: "Mobile App Developer",
      icon: <FaMobile size={48} />,
      category: "Development",
      description: "Creating innovative mobile applications for iOS and Android platforms with modern development practices.",
      responsibilities: [
        "iOS and Android app development",
        "UI/UX implementation and testing",
        "App store optimization and deployment",
        "Performance monitoring and updates"
      ],
      requirements: [
        "3+ years of mobile development",
        "Experience with Flutter, React Native, or native",
        "Knowledge of mobile app lifecycle",
        "Understanding of mobile UI/UX principles"
      ]
    },
    {
      id: 6,
      title: "Social Media Marketing Specialist",
      icon: <FaBullhorn size={48} />,
      category: "Marketing",
      description: "Developing and executing social media strategies to enhance brand presence and engage target audiences.",
      responsibilities: [
        "Social media strategy and content creation",
        "Community management and engagement",
        "Analytics and performance tracking",
        "Campaign planning and execution"
      ],
      requirements: [
        "2+ years of social media marketing",
        "Experience with major platforms",
        "Content creation and copywriting skills",
        "Analytics and reporting knowledge"
      ]
    },
    {
      id: 7,
      title: "Data Analyst",
      icon: <FaChartLine size={48} />,
      category: "Analytics",
      description: "Analyzing data to provide insights that drive business decisions and improve operational efficiency.",
      responsibilities: [
        "Data collection and analysis",
        "Report generation and visualization",
        "Trend identification and forecasting",
        "Business intelligence and insights"
      ],
      requirements: [
        "3+ years of data analysis experience",
        "Proficiency in SQL, Python, and BI tools",
        "Statistical analysis and modeling skills",
        "Strong analytical and critical thinking"
      ]
    },
    {
      id: 8,
      title: "DevOps Engineer",
      icon: <FaCogs size={48} />,
      category: "Operations",
      description: "Managing infrastructure, deployment pipelines, and ensuring smooth software delivery operations.",
      responsibilities: [
        "CI/CD pipeline management",
        "Infrastructure automation and monitoring",
        "Cloud platform management",
        "Security and performance optimization"
      ],
      requirements: [
        "4+ years of DevOps experience",
        "Experience with AWS, Docker, and Kubernetes",
        "Automation and scripting skills",
        "Understanding of security best practices"
      ]
    },
    {
      id: 9,
      title: "Cybersecurity Specialist",
      icon: <FaShieldAlt size={48} />,
      category: "Security",
      description: "Protecting company assets and ensuring compliance with security standards and best practices.",
      responsibilities: [
        "Security assessment and testing",
        "Incident response and threat monitoring",
        "Security policy development",
        "Compliance and risk management"
      ],
      requirements: [
        "5+ years of cybersecurity experience",
        "Relevant certifications (CISSP, CEH)",
        "Knowledge of security frameworks",
        "Experience with security tools and technologies"
      ]
    },
    {
      id: 10,
      title: "Innovation Specialist",
      icon: <FaLightbulb size={48} />,
      category: "Innovation",
      description: "Driving innovation initiatives and exploring emerging technologies to maintain competitive advantage.",
      responsibilities: [
        "Technology trend research and analysis",
        "Innovation project management",
        "Partnership and collaboration development",
        "Innovation culture promotion"
      ],
      requirements: [
        "3+ years of innovation or R&D experience",
        "Strong research and analytical skills",
        "Knowledge of emerging technologies",
        "Creative thinking and problem-solving"
      ]
    }
  ];

  const categories = ["Leadership", "Management", "HR", "Development", "Marketing", "Analytics", "Operations", "Security", "Innovation"];

  return (
    <div className="roles-page">
      {/* Hero Section */}
      <section className="roles-hero">
        <Container>
          <div className="text-center">
            <h1 className="roles-hero-title">Meet Our Team</h1>
            <p className="roles-hero-subtitle">
              Discover the talented professionals who drive our company's success and innovation
            </p>
            <div className="roles-hero-stats">
              <div className="stat-item">
                <h3>10+</h3>
                <p>Team Members</p>
              </div>
              <div className="stat-item">
                <h3>50+</h3>
                <p>Years Combined Experience</p>
              </div>
              <div className="stat-item">
                <h3>9</h3>
                <p>Specialized Areas</p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Categories Filter */}
      <section className="categories-section">
        <Container>
          <div className="categories-filter">
            <h3 className="categories-title">Filter by Department</h3>
            <div className="categories-buttons">
              {categories.map((category) => (
                <button key={category} className="category-filter-btn active">
                  {category}
                </button>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Team Members Grid */}
      <section className="roles-grid-section">
        <Container>
          <Row className="g-4">
            {roles.map((role) => (
              <Col key={role.id} xs={12} md={6} lg={4}>
                <Card className="role-card h-100">
                  <Card.Body className="role-card-body">
                    {/* Role Icon and Category */}
                    <div className="role-header mb-3">
                      <div className="role-icon">
                        {role.icon}
                      </div>
                      <Badge bg="primary" className="role-category">
                        {role.category}
                      </Badge>
                    </div>

                    {/* Role Title */}
                    <Card.Title className="role-title mb-3">
                      {role.title}
                    </Card.Title>

                    {/* Role Description */}
                    <Card.Text className="role-description mb-4">
                      {role.description}
                    </Card.Text>

                    {/* Responsibilities */}
                    <div className="role-section mb-3">
                      <h6 className="role-section-title">Key Responsibilities:</h6>
                      <ul className="role-list">
                        {role.responsibilities.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Requirements */}
                    <div className="role-section mb-4">
                      <h6 className="role-section-title">Experience & Skills:</h6>
                      <ul className="role-list">
                        {role.requirements.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>

                    {/* View Details Button */}
                    <div className="role-apply-section">
                      <button 
                        className="role-view-details-btn"
                        onClick={() => handleViewDetails(role.id)}
                      >
                        View Details →
                      </button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="roles-cta-section">
        <Container>
          <div className="text-center">
            <h2 className="cta-title">Our Team Drives Innovation</h2>
            <p className="cta-subtitle">
              Each team member brings unique expertise and passion to deliver exceptional results for our clients.
            </p>
            <button className="cta-button">
              Learn More About Us
            </button>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default RolesPage;
